import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';
import axios from 'axios';
import { toWords } from 'number-to-words';
import { BASE_URL } from '../../../services/Api';
import { authHeader } from '../../../services/auth-header';
import '../PumpSpareDomestic/sales-invoice-print.css';


const SalesInvoiceManufacturingDomesticPrint = () => {
    const { id } = useParams();
    const [isLoading, setIsLoading] = useState(true);
    const [salesData, setSalesData] = useState(null);
    const [rows, setRows] = useState([]);
    const [additionalCharges, setAdditionalCharges] = useState([]);
    const totalProductRows = 8; // Always show 8 product rows on first page

    const fetchSalesData = async () => {
        try {
            const response = await axios.get(`${BASE_URL}sales/findById/${id}`, {
                headers: authHeader(),
            });

            const data = response.data;
            setSalesData(data);
            console.log("Fetched sales data:", data);

            // Check if same state (Maharashtra) or different state
            const isSameState = data.billto?.state?.statename === "Maharashtra";

            if (data.salesItems && data.salesItems.length > 0) {
                const itemsWithGst = data.salesItems.map(item => {
                    const gstPercent = parseFloat(item.gst) || parseFloat(item.product?.igst) || 0;
                    const quantity = parseFloat(item.quantity) || 0;
                    const unitRate = parseFloat(item.unitrateinr) || 0;

                    // Calculate the base amount (GST exclusive)
                    const baseAmount = quantity * unitRate;

                    if (isSameState) {
                        // Same state: Split GST into CGST and SGST
                        const gstAmount = baseAmount * (gstPercent / 100);
                        const halfGstPercent = gstPercent / 2;
                        const halfGstAmount = gstAmount / 2;

                        return {
                            description: item.product?.description || '-',
                            quantity: quantity,
                            unitrateinr: unitRate,
                            amount: baseAmount + gstAmount,
                            baseAmount: baseAmount,
                            hsnsacno: item.hsnsacno || "-",
                            uom: item.uom || "-",
                            gstPercent: gstPercent,
                            taxableValue: baseAmount,
                            gstAmount: gstAmount,
                            cgstPercent: halfGstPercent,
                            sgstPercent: halfGstPercent,
                            cgstAmount: halfGstAmount,
                            sgstAmount: halfGstAmount,
                            isSameState: true
                        };
                    } else {
                        // Different state: Apply IGST only
                        const igstAmount = baseAmount * (gstPercent / 100);

                        return {
                            description: item.product?.description || '-',
                            quantity: quantity,
                            unitrateinr: unitRate,
                            amount: baseAmount + igstAmount,
                            baseAmount: baseAmount,
                            hsnsacno: item.hsnsacno || "-",
                            uom: item.uom || "-",
                            gstPercent: gstPercent,
                            taxableValue: baseAmount,
                            igstAmount: igstAmount,
                            igstPercent: gstPercent,
                            isSameState: false
                        };
                    }
                });
                setRows(itemsWithGst);
            }

            if (data.additionalCharges && data.additionalCharges.length > 0) {
                const chargesWithGst = data.additionalCharges.map(charge => {
                    const chargeGstPercent = parseFloat(charge.chargegst) || 0;
                    const chargeBaseAmount = parseFloat(charge.chargeamount) || 0;

                    if (isSameState) {
                        // Same state: Split GST into CGST and SGST
                        const chargeGstAmount = chargeBaseAmount * (chargeGstPercent / 100);
                        const chargeTotal = chargeBaseAmount + chargeGstAmount;

                        return {
                            ...charge,
                            chargeamount: chargeBaseAmount,
                            chargegst: chargeGstPercent,
                            chargetotal: chargeTotal,
                            cgstAmount: chargeGstAmount / 2,
                            sgstAmount: chargeGstAmount / 2,
                            gstAmount: chargeGstAmount,
                            isSameState: true
                        };
                    } else {
                        // Different state: Apply IGST only
                        const chargeIgstAmount = chargeBaseAmount * (chargeGstPercent / 100);
                        const chargeTotal = chargeBaseAmount + chargeIgstAmount;

                        return {
                            ...charge,
                            chargeamount: chargeBaseAmount,
                            chargegst: chargeGstPercent,
                            chargetotal: chargeTotal,
                            igstAmount: chargeIgstAmount,
                            isSameState: false
                        };
                    }
                });
                setAdditionalCharges(chargesWithGst);
            }
        } catch (error) {
            console.error("Error fetching sales data", error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchSalesData();
    }, [id]);

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        const day = date.getDate().toString().padStart(2, '0');
        const month = date.toLocaleString('en-US', { month: 'short' });
        const year = date.getFullYear().toString().slice(-2);
        return `${day}-${month}-${year}`;
    };

    const formatNumberWithCommas = (num) => {
        if (isNaN(num) || num === null || num === undefined) return '0.00';
        return parseFloat(num).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    };

    const numberToWordsWithRupees = (num) => {
        if (isNaN(num) || num === null || num === undefined) {
            return "Zero Rupees Only";
        }

        const amount = Math.round(Number(num));
        const words = toWords(amount);

        const capitalizedWords = words.split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ');

        return `INR ${capitalizedWords} Only`;
    };

    if (isLoading) {
        return (
            <div style={{
                padding: "2px",
                fontFamily: "Arial, sans-serif",
                margin: 0,
                fontSize: "11px",
                textAlign: "center",
                paddingTop: "50px",
                paddingBottom: "50px"
            }}>
                <Spinner animation="border" variant="primary" />
                <p style={{ marginTop: "10px" }}>Loading sales order details...</p>
            </div>
        );
    }

    if (!salesData) {
        return (
            <div style={{
                padding: "2px",
                fontFamily: "Arial, sans-serif",
                margin: 0,
                fontSize: "11px",
                textAlign: "center",
                paddingTop: "50px",
                paddingBottom: "50px"
            }}>
                <div className="alert alert-warning">No sales order data found</div>
            </div>
        );
    }

    // Check if same state (Maharashtra) or different state
    const isSameState = salesData.billto?.state?.statename === "Maharashtra";

    // Calculate values for products
    const productsTaxableValue = rows.reduce((sum, row) => sum + (parseFloat(row.taxableValue) || 0), 0);
    const productsGstAmount = rows.reduce((sum, row) => sum + (parseFloat(row.gstAmount || row.igstAmount) || 0), 0);

    // Calculate values for additional charges
    const chargesTaxableValue = additionalCharges.reduce((sum, charge) =>
        sum + (parseFloat(charge.chargeamount) || 0), 0);
    const chargesGstAmount = additionalCharges.reduce((sum, charge) =>
        sum + (parseFloat(charge.gstAmount || charge.igstAmount) || 0), 0);

    // Calculate totals
    const totalTaxableValue = productsTaxableValue + chargesTaxableValue;
    const totalGstAmount = productsGstAmount + chargesGstAmount;
    const grandTotal = totalTaxableValue + totalGstAmount;
    const roundOff = parseFloat((Math.round(grandTotal) - grandTotal).toFixed(2));
    const finalGrandTotal = Math.round(grandTotal);

    // Calculate CGST, SGST, and IGST totals
    let cgstTotal = 0;
    let sgstTotal = 0;
    let igstTotal = 0;

    if (isSameState) {
        cgstTotal = rows.reduce((sum, row) => sum + (parseFloat(row.cgstAmount) || 0), 0) +
            additionalCharges.reduce((sum, charge) => sum + (parseFloat(charge.cgstAmount) || 0), 0);
        sgstTotal = rows.reduce((sum, row) => sum + (parseFloat(row.sgstAmount) || 0), 0) +
            additionalCharges.reduce((sum, charge) => sum + (parseFloat(charge.sgstAmount) || 0), 0);
    } else {
        igstTotal = rows.reduce((sum, row) => sum + (parseFloat(row.igstAmount) || 0), 0) +
            additionalCharges.reduce((sum, charge) => sum + (parseFloat(charge.igstAmount) || 0), 0);
    }

    const totalQuantity = rows.reduce((sum, row) => sum + parseFloat(row.quantity || 0), 0);

    // Extract company data from branch
    const companyData = salesData.company || {};
    const companyAddress = companyData.companyMultipleAddresses?.[0];

    // Calculate empty rows needed for first page
    const actualRows = rows.length;
    const emptyRowsNeeded = Math.max(0, totalProductRows - actualRows);

    const handlePrint = () => {
        const printStyle = document.createElement('style');
        printStyle.innerHTML = `
        @page {
            margin: 0.7cm 0.5cm 1.2cm 0.5cm;
            
            /* REMOVE ALL FOOTERS */
            @bottom-left { content: none; }
            @bottom-center { content: none; }
            @bottom-right { content: none; }
        }
        
        @media print {
            body { 
                margin: 0 !important;
                padding: 0 !important;
                font-size: 11px !important;
            }
            
            .sip-print-button { 
                display: none !important; 
            }
            
            /* FORCE TAX INVOICE CENTER IN PRINT */
            .sip-title {
                font-size: 19px !important;
                margin: 0 !important;
                padding: 0 !important;
                font-weight: bold !important;
            }
            
            .sip-logo {
                width: 100px !important;
                height: 100px !important;
            }
            
            .sip-logo img {
                width: 100% !important;
                height: 100% !important;
                object-fit: contain !important;
            }
            
            /* SIGNATURE SECTION FIX */
            .sip-signature-section {
                padding-top: 0px !important;
                position: relative !important;
            }
            
            .sip-signature-section > div:first-child {
                margin-bottom: 30px !important;
            }
            
            /* PREVENT BOTTOM SECTION CUTTING */
            .sip-bottom-section {
                page-break-inside: avoid !important;
                break-inside: avoid !important;
            }
        }
    `;
        document.head.appendChild(printStyle);

        window.print();

        setTimeout(() => {
            document.head.removeChild(printStyle);
        }, 1000);
    };

    const headerStyle = {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        margin: '0',
        padding: '0'
    };

    const titleStyle = {
        fontSize: '19px',
        fontWeight: 'bold',
        textAlign: 'center',
        textTransform: 'uppercase',
        flexGrow: '1',
        margin: '0',
        padding: '0'
    };

    const logoStyle = {
        width: '100px',
        height: '100px',
        overflow: 'hidden',
        margin: '0',
        padding: '0'
    };

    const logoImgStyle = {
        height: '100%',
        width: '100%',
        objectFit: 'contain'
    };

    return (
        <div style={{
            padding: "2px",
            fontFamily: "Arial, sans-serif",
            margin: 0,
            fontSize: "11px",
            width: "21cm",
            minHeight: "29.7cm",
            padding: "0.5cm",
            margin: "0 auto",
            boxShadow: "0 0 5px rgba(0, 0, 0, 0.1)",
            background: "white"
        }}>

            <div className="sip-print-button d-flex gap-2 justify-content-center" style={{ marginBottom: "10px", textAlign: "center", padding: "2px" }}>
                <button className="btn btn-primary" onClick={handlePrint}>
                    🖨️ Print Invoice
                </button>
            </div>

            {/* Printable Content */}
            <div
                id="invoice-print-content"
                style={{
                    width: "100%",
                    pageBreakInside: "avoid",
                    backgroundColor: 'white',
                    fontSize: '11px'
                }}
            >
                {/* Compact Header Section - LOGO ON RIGHT SIDE */}
                <div style={headerStyle}>
                    <div style={titleStyle} className="sip-title">TAX INVOICE</div>
                    <div style={logoStyle} className="sip-logo">
                        <img
                            src="/assets/company-logo.png"
                            alt="Company Logo"
                            style={logoImgStyle}
                        />
                    </div>
                </div>

                {/* Company and Customer Details Section */}
                <table style={{ width: '100%', border: '1px solid black', borderCollapse: 'collapse' }}>
                    <tbody>
                        <tr>
                            {/* Left Side – Company + Consignee + Buyer */}
                            <td
                                style={{
                                    width: "50%",
                                    borderRight: "1px solid black",
                                    padding: "2px",
                                    verticalAlign: "top",
                                }}
                            >
                                {/* Company Info */}
                                <div style={{ marginBottom: "0px", fontSize: '11px', padding: '2px' }}>
                                    <strong style={{ fontSize: '11px', fontStyle: 'italic', fontWeight: 'bold' }}>{companyData.companyname || "SYNERGY Mechanics Pvt Ltd"}</strong><br />
                                    Works :- {companyAddress?.address || "W-95/96 MIDC , Phase II, Dombivli (E)-421203"}<br />
                                    Mobile No: {companyData.workcontactno || "8149306088"} | Office: {companyData.officecontactno || "N/A"}<br />
                                    UDYAM Reg No.: {companyData.udyamregistrationno || "UDYAM-MH-33-0004552 (Small)"}  <br />
                                    GSTIN/UIN: {companyData.gstno || "27AANCS4868A1Z3"} <br />
                                    State Name: {companyAddress?.state?.statename || "Maharashtra"}, Code: {companyAddress?.state?.statecode || "27"}<br />
                                    E-Mail: {companyData.email || "office@synergymechanics.com"}
                                </div>

                                {/* Consignee */}
                                <div style={{ borderTop: "1px solid black", paddingTop: "2px", marginBottom: "0px", fontSize: '11px', padding: '2px' }}>
                                    Consignee (Ship to)<br />
                                    <strong style={{ fontSize: '11px' }}> {salesData.customer?.companyname || "N/A"}</strong><br />
                                    {salesData.shipto?.customeraddress || "N/A"}, {salesData.shipto?.city?.cityname || "N/A"}<br />
                                    Kind Attn: {salesData.kindattn || "N/A"} <br />
                                    Contact No.: {salesData.customer?.contactno || "N/A"} <br />
                                    GSTIN/UIN: {salesData.customer?.gstno || "N/A"}<br />
                                    State Name: {salesData.shipto?.state?.statename || "N/A"}, Code: {salesData.shipto?.state?.statecode || "N/A"}
                                </div>

                                {/* Buyer */}
                                <div style={{ borderTop: "1px solid black", paddingTop: "2px", fontSize: '11px', padding: '2px' }}>
                                    Buyer (Bill to)<br />
                                    <strong style={{ fontSize: '11px' }}> {salesData.customer?.companyname || "N/A"}</strong><br />
                                    {salesData.billto?.customeraddress || "N/A"}, {salesData.billto?.city?.cityname || "N/A"}<br />
                                    Kind Attn: {salesData.kindattn || "N/A"} <br />
                                    Contact No.: {salesData.customer?.contactno || "N/A"} <br />
                                    GSTIN/UIN: {salesData.customer?.gstno || "N/A"}<br />
                                    State Name: {salesData.billto?.state?.statename || "N/A"}, Code: {salesData.billto?.state?.statecode || "N/A"}<br />
                                    Place of Supply: {salesData.billto?.state?.statename || "N/A"}
                                </div>
                            </td>

                            {/* Right Side – Invoice Details Table */}
                            <td style={{ width: '50%', padding: '2px', verticalAlign: 'top' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <tbody>
                                        <tr style={{ borderTop: 'none' }}>
                                            <td style={{ borderTop: 'none', padding: '2px' }}>
                                                <span style={{ fontSize: '11px' }}>Invoice No. :</span><br />
                                                <strong style={{ fontSize: '11px' }}>{salesData.invoiceno || 'N/A'}</strong>
                                            </td>
                                            <td style={{ borderTop: 'none', borderLeft: '1px solid black', padding: '2px' }}>
                                                <span style={{ fontSize: '11px' }}>Dated :</span><br />
                                                <strong style={{ fontSize: '11px' }}>{formatDate(salesData.invoicedate) || 'N/A'}</strong>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td style={{ borderTop: '1px solid black', borderLeft: 'none', padding: '2px' }}>
                                                <span style={{ fontSize: '11px' }}>Delivery Note :</span><br />
                                                <strong style={{ fontSize: '11px' }}>{salesData.dcno || 'N/A'}</strong>
                                            </td>
                                            <td style={{ borderTop: '1px solid black', borderLeft: '1px solid black', padding: '2px' }}>
                                                <span style={{ fontSize: '11px' }}>Delivery Note Date :</span><br />
                                                <strong style={{ fontSize: '11px' }}>{formatDate(salesData.dcdate) || 'N/A'}</strong>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td style={{ borderTop: '1px solid black', borderLeft: 'none', padding: '2px' }}>
                                                <span style={{ fontSize: '11px' }}>Reference No. & Date :</span><br />
                                                <strong style={{ fontSize: '11px' }}>{salesData.referenceno || 'N/A'}</strong>
                                            </td>
                                            <td style={{ borderTop: '1px solid black', borderLeft: '1px solid black', padding: '2px' }}>
                                                <span style={{ fontSize: '11px' }}>Other References :</span><br />
                                                <strong style={{ fontSize: '11px' }}>{salesData.reference || 'N/A'}</strong>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td style={{ borderTop: '1px solid black', borderLeft: 'none', padding: '2px' }}>
                                                <span style={{ fontSize: '11px' }}>Buyer's Order No. :</span><br />
                                                <strong style={{ fontSize: '11px' }}>{salesData.buyerorderno || 'N/A'}</strong>
                                            </td>
                                            <td style={{ borderTop: '1px solid black', borderLeft: '1px solid black', padding: '2px' }}>
                                                <span style={{ fontSize: '11px' }}>Dated :</span><br />
                                                <strong style={{ fontSize: '11px' }}>{formatDate(salesData.buyerorderdate) || 'N/A'}</strong>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td style={{ borderTop: '1px solid black', borderLeft: 'none', padding: '2px' }}>
                                                <span style={{ fontSize: '11px' }}>Dispatch Through :</span><br />
                                                <strong style={{ fontSize: '11px' }}>{salesData.dispatchthrough || salesData.dcno || 'N/A'}</strong>
                                            </td>
                                            <td style={{ borderTop: '1px solid black', borderLeft: '1px solid black', padding: '2px' }}>
                                                <span style={{ fontSize: '11px' }}>Mode/Terms of Payment :</span><br />
                                                <strong style={{ fontSize: '11px' }}>{salesData.customer?.paymentterm || 'N/A'}</strong>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td style={{ borderTop: '1px solid black', borderLeft: 'none', padding: '2px' }} colSpan="2">
                                                <span style={{ fontSize: '11px' }}>Terms of Delivery :</span><br />
                                                <strong style={{ fontSize: '11px' }}>{salesData.termsofdelivery || 'N/A'}</strong>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </td>
                        </tr>
                    </tbody>
                </table>

                {/* Product Table */}
                <div style={{ marginTop: '2px' }}>
                    <table style={{
                        width: '100%',
                        tableLayout: 'fixed',
                        pageBreakInside: 'auto',
                        borderCollapse: 'collapse',
                        marginBottom: '2px',
                        fontSize: '11px'
                    }}>
                        <thead>
                            <tr>
                                <th style={{ width: '5%', fontSize: '11px', padding: '2px', border: '1px solid black', textAlign: 'center', fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>Sl No.</th>
                                <th style={{ width: '30%', fontSize: '11px', padding: '2px', border: '1px solid black', textAlign: 'center', fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>Description of Goods</th>
                                <th style={{ width: '10%', fontSize: '11px', padding: '2px', border: '1px solid black', textAlign: 'center', fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>HSN/SAC</th>
                                <th style={{ width: '8%', fontSize: '11px', padding: '2px', border: '1px solid black', textAlign: 'center', fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>GST %</th>
                                <th style={{ width: '10%', fontSize: '11px', padding: '2px', border: '1px solid black', textAlign: 'center', fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>Quantity</th>
                                <th style={{ width: '10%', fontSize: '11px', padding: '2px', border: '1px solid black', textAlign: 'center', fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>Rate</th>
                                <th style={{ width: '10%', fontSize: '11px', padding: '2px', border: '1px solid black', textAlign: 'center', fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>Per</th>
                                <th style={{ width: '17%', fontSize: '11px', padding: '2px', border: '1px solid black', textAlign: 'center', fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>Amount</th>
                            </tr>
                        </thead>
                        <tbody style={{
                            display: 'table-row-group',
                            pageBreakInside: 'auto'
                        }}>
                            {/* Actual Product Rows */}
                            {rows.map((row, index) => (
                                <tr key={index}>
                                    <td style={{ fontSize: '11px', padding: '2px', border: '1px solid black', textAlign: 'center' }}>{index + 1}</td>
                                    <td style={{ fontSize: '11px', padding: '2px', border: '1px solid black', textAlign: 'center' }}>
                                        <strong style={{ fontSize: '11px' }}>{row.description}</strong>
                                    </td>
                                    <td style={{ fontSize: '11px', padding: '2px', border: '1px solid black', textAlign: 'center' }}>{row.hsnsacno}</td>
                                    <td style={{ fontSize: '11px', padding: '2px', border: '1px solid black', textAlign: 'center' }}>{row.gstPercent}%</td>
                                    <td style={{ fontSize: '11px', padding: '2px', border: '1px solid black', textAlign: 'center' }}>{row.quantity} {row.uom}</td>
                                    <td style={{ fontSize: '11px', padding: '2px', border: '1px solid black', textAlign: 'right' }}>{formatNumberWithCommas(row.unitrateinr)}</td>
                                    <td style={{ fontSize: '11px', padding: '2px', border: '1px solid black', textAlign: 'center' }}>{row.uom}</td>
                                    <td style={{ fontSize: '11px', padding: '2px', border: '1px solid black', textAlign: 'right' }}>{formatNumberWithCommas(row.amount)}</td>
                                </tr>
                            ))}

                            {/* Empty Rows to ALWAYS fill 8 rows on first page */}
                            {Array.from({ length: emptyRowsNeeded }).map((_, index) => (
                                <tr key={`empty-${index}`}>
                                    <td style={{ fontSize: '11px', padding: '2px', border: '1px solid black', textAlign: 'center' }}>&nbsp;</td>
                                    <td style={{ fontSize: '11px', padding: '2px', border: '1px solid black' }}>&nbsp;</td>
                                    <td style={{ fontSize: '11px', padding: '2px', border: '1px solid black', textAlign: 'center' }}>&nbsp;</td>
                                    <td style={{ fontSize: '11px', padding: '2px', border: '1px solid black', textAlign: 'center' }}>&nbsp;</td>
                                    <td style={{ fontSize: '11px', padding: '2px', border: '1px solid black', textAlign: 'center' }}>&nbsp;</td>
                                    <td style={{ fontSize: '11px', padding: '2px', border: '1px solid black', textAlign: 'right' }}>&nbsp;</td>
                                    <td style={{ fontSize: '11px', padding: '2px', border: '1px solid black', textAlign: 'center' }}>&nbsp;</td>
                                    <td style={{ fontSize: '11px', padding: '2px', border: '1px solid black', textAlign: 'right' }}>&nbsp;</td>
                                </tr>
                            ))}

                            {/* Additional Charges */}
                            {additionalCharges.map((charge, index) => (
                                <tr key={`charge-${index}`}>
                                    <td colSpan="6" style={{ verticalAlign: 'top', paddingTop: '2px', fontSize: '11px', padding: '2px', border: '1px solid black', textAlign: 'right' }}>
                                        <div>
                                            <strong style={{ fontSize: '11px' }}>
                                                <i style={{ fontSize: '11px' }}>
                                                    {charge.chargetype} @ {charge.chargepercent}%
                                                </i>
                                            </strong><br />
                                        </div>
                                    </td>
                                    <td style={{ fontSize: '11px', padding: '2px', border: '1px solid black', textAlign: 'right', fontWeight: 'bold' }}>Add :</td>
                                    <td style={{ fontSize: '11px', padding: '2px', border: '1px solid black', textAlign: 'right' }}>
                                        {formatNumberWithCommas(charge.chargetotal)}
                                    </td>
                                </tr>
                            ))}

                            {/* GST and Round Off Display Row */}
                            <tr>
                                <td colSpan="6" rowSpan={isSameState ? 3 : 2} style={{ verticalAlign: "top", paddingTop: "2px", fontSize: '11px', padding: '2px', border: '1px solid black', textAlign: 'right' }}>
                                    <div>
                                        {isSameState ? (
                                            <>
                                                <strong style={{ fontSize: '11px' }}>
                                                    <i style={{ fontSize: '11px' }}>CGST OUTPUT</i>
                                                </strong><br />
                                                <strong style={{ fontSize: '11px' }}>
                                                    <i style={{ fontSize: '11px' }}>SGST OUTPUT</i>
                                                </strong><br />
                                            </>
                                        ) : (
                                            <strong style={{ fontSize: '11px' }}>
                                                <i style={{ fontSize: '11px' }}>IGST OUTPUT</i>
                                            </strong>
                                        )}
                                        <strong style={{ fontSize: '11px' }}>
                                            <i style={{ fontSize: '11px' }}>Round Off</i>
                                        </strong><br />
                                    </div>
                                </td>

                                {isSameState ? (
                                    <>
                                        <td style={{ fontSize: '11px', padding: '2px', border: '1px solid black', textAlign: 'right', fontWeight: 'bold' }}>Add :</td>
                                        <td style={{ padding: '2px', border: '1px solid black', textAlign: 'right' }}>{formatNumberWithCommas(cgstTotal)}</td>
                                    </>
                                ) : (
                                    <>
                                        <td style={{ fontSize: '11px', padding: '2px', border: '1px solid black', textAlign: 'right', fontWeight: 'bold' }}>Add :</td>
                                        <td style={{ padding: '2px', border: '1px solid black', textAlign: 'right' }}>{formatNumberWithCommas(igstTotal)}</td>
                                    </>
                                )}
                            </tr>
                            {isSameState && (
                                <tr>
                                    <td style={{ fontSize: '11px', padding: '2px', border: '1px solid black', textAlign: 'right', fontWeight: 'bold' }}>Add :</td>
                                    <td style={{ fontSize: '11px', padding: '2px', border: '1px solid black', textAlign: 'right' }}>{formatNumberWithCommas(sgstTotal)}</td>
                                </tr>
                            )}
                            <tr>
                                <td style={{ fontSize: '11px', padding: '2px', border: '1px solid black', textAlign: 'right', fontWeight: 'bold' }}>Adjust :</td>
                                <td style={{ fontSize: '11px', padding: '2px', border: '1px solid black', textAlign: 'right' }}>{formatNumberWithCommas(roundOff)}</td>
                            </tr>

                            {/* Total Row */}
                            <tr>
                                <td colSpan="4" style={{ fontSize: '11px', padding: '2px', border: '1px solid black', textAlign: 'right', fontWeight: 'bold' }}>Total</td>
                                <td style={{ fontSize: '11px', padding: '2px', border: '1px solid black', textAlign: 'center', fontWeight: 'bold' }}>
                                    {formatNumberWithCommas(totalQuantity)}
                                </td>
                                <td colSpan={isSameState ? 2 : 2} style={{ padding: '2px', border: '1px solid black' }}></td>
                                <td style={{ fontSize: '11px', padding: '2px', border: '1px solid black', textAlign: 'right', fontWeight: 'bold' }}>₹ {formatNumberWithCommas(finalGrandTotal)}</td>
                            </tr>
                            <tr>
                                <td colSpan="8" style={{ textAlign: 'left', paddingTop: '2px', fontSize: '11px', padding: '2px', border: '1px solid black' }}>
                                    Amount Chargeable (in words): <strong style={{ fontSize: '11px' }}>{numberToWordsWithRupees(finalGrandTotal)}</strong>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                {/* Tax Table */}
                <table style={{
                    marginTop: '2px',
                    border: '1px solid black',
                    fontSize: '11px',
                    pageBreakInside: 'avoid',
                    width: '100%',
                    borderCollapse: 'collapse'
                }}>
                    <thead>
                        <tr>
                            <th style={{ fontSize: '11px', width: '10%', padding: '2px', border: '1px solid black', textAlign: 'center', fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>HSN/SAC</th>
                            <th style={{ fontSize: '11px', width: '15%', padding: '2px', border: '1px solid black', textAlign: 'center', fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>Taxable Value</th>
                            {isSameState ? (
                                <>
                                    <th style={{ fontSize: '11px', width: '5%', padding: '2px', border: '1px solid black', textAlign: 'center', fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>CGST</th>
                                    <th style={{ fontSize: '11px', width: '5%', padding: '2px', border: '1px solid black', textAlign: 'center', fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>Rate</th>
                                    <th style={{ fontSize: '11px', width: '10%', padding: '2px', border: '1px solid black', textAlign: 'center', fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>Amount</th>
                                    <th style={{ fontSize: '11px', width: '10%', padding: '2px', border: '1px solid black', textAlign: 'center', fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>SGST/UTGST</th>
                                    <th style={{ fontSize: '11px', width: '5%', padding: '2px', border: '1px solid black', textAlign: 'center', fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>Rate</th>
                                    <th style={{ fontSize: '11px', width: '10%', padding: '2px', border: '1px solid black', textAlign: 'center', fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>Amount</th>
                                </>
                            ) : (
                                <>
                                    <th style={{ fontSize: '11px', width: '5%', padding: '2px', border: '1px solid black', textAlign: 'center', fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>IGST</th>
                                    <th style={{ fontSize: '11px', width: '5%', padding: '2px', border: '1px solid black', textAlign: 'center', fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>Rate</th>
                                    <th style={{ fontSize: '11px', width: '10%', padding: '2px', border: '1px solid black', textAlign: 'center', fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>Amount</th>
                                    <th style={{ fontSize: '11px', width: '10%', padding: '2px', border: '1px solid black', textAlign: 'center', fontWeight: 'bold', backgroundColor: '#f8f9fa' }} colSpan="3"></th>
                                </>
                            )}
                            <th style={{ fontSize: '11px', width: '15%', padding: '2px', border: '1px solid black', textAlign: 'center', fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>Total Tax Amount</th>
                        </tr>
                    </thead>
                    <tbody>
                        {/* Product GST Rows */}
                        {rows.map((row, index) => (
                            <tr key={`gst-${index}`}>
                                <td style={{ fontSize: '11px', padding: '2px', border: '1px solid black', textAlign: 'center' }}>{row.hsnsacno}</td>
                                <td style={{ fontSize: '11px', padding: '2px', border: '1px solid black', textAlign: 'right' }}>{formatNumberWithCommas(row.taxableValue)}</td>
                                {isSameState ? (
                                    <>
                                        <td style={{ padding: '2px', border: '1px solid black' }}></td>
                                        <td style={{ fontSize: '11px', padding: '2px', border: '1px solid black', textAlign: 'center' }}>{row.cgstPercent}%</td>
                                        <td style={{ fontSize: '11px', padding: '2px', border: '1px solid black', textAlign: 'right' }}>{formatNumberWithCommas(row.cgstAmount)}</td>
                                        <td style={{ padding: '2px', border: '1px solid black' }}></td>
                                        <td style={{ fontSize: '11px', padding: '2px', border: '1px solid black', textAlign: 'center' }}>{row.sgstPercent}%</td>
                                        <td style={{ fontSize: '11px', padding: '2px', border: '1px solid black', textAlign: 'right' }}>{formatNumberWithCommas(row.sgstAmount)}</td>
                                    </>
                                ) : (
                                    <>
                                        <td style={{ padding: '2px', border: '1px solid black' }}></td>
                                        <td style={{ fontSize: '11px', padding: '2px', border: '1px solid black', textAlign: 'center' }}>{row.igstPercent}%</td>
                                        <td style={{ fontSize: '11px', padding: '2px', border: '1px solid black', textAlign: 'right' }}>{formatNumberWithCommas(row.igstAmount)}</td>
                                        <td colSpan="3" style={{ padding: '2px', border: '1px solid black' }}></td>
                                    </>
                                )}
                                <td style={{ fontSize: '11px', padding: '2px', border: '1px solid black', textAlign: 'right' }}>
                                    {formatNumberWithCommas(row.gstAmount || row.igstAmount)}
                                </td>
                            </tr>
                        ))}

                        {/* Additional Charges GST Rows */}
                        {additionalCharges.map((charge, index) => (
                            <tr key={`charge-gst-${index}`}>
                                <td style={{ fontSize: '11px', padding: '2px', border: '1px solid black', textAlign: 'center' }}>-</td>
                                <td style={{ fontSize: '11px', padding: '2px', border: '1px solid black', textAlign: 'right' }}>{formatNumberWithCommas(charge.chargeamount)}</td>
                                {isSameState ? (
                                    <>
                                        <td style={{ padding: '2px', border: '1px solid black' }}></td>
                                        <td style={{ fontSize: '11px', padding: '2px', border: '1px solid black', textAlign: 'center' }}>{charge.chargegst / 2}%</td>
                                        <td style={{ fontSize: '11px', padding: '2px', border: '1px solid black', textAlign: 'right' }}>{formatNumberWithCommas(charge.cgstAmount)}</td>
                                        <td style={{ padding: '2px', border: '1px solid black' }}></td>
                                        <td style={{ fontSize: '11px', padding: '2px', border: '1px solid black', textAlign: 'center' }}>{charge.chargegst / 2}%</td>
                                        <td style={{ fontSize: '11px', padding: '2px', border: '1px solid black', textAlign: 'right' }}>{formatNumberWithCommas(charge.sgstAmount)}</td>
                                    </>
                                ) : (
                                    <>
                                        <td style={{ padding: '2px', border: '1px solid black' }}></td>
                                        <td style={{ fontSize: '11px', padding: '2px', border: '1px solid black', textAlign: 'center' }}>{charge.chargegst}%</td>
                                        <td style={{ fontSize: '11px', padding: '2px', border: '1px solid black', textAlign: 'right' }}>{formatNumberWithCommas(charge.igstAmount)}</td>
                                        <td colSpan="3" style={{ padding: '2px', border: '1px solid black' }}></td>
                                    </>
                                )}
                                <td style={{ fontSize: '11px', padding: '2px', border: '1px solid black', textAlign: 'right' }}>
                                    {formatNumberWithCommas(charge.gstAmount || charge.igstAmount)}
                                </td>
                            </tr>
                        ))}

                        <tr>
                            <td colSpan={isSameState ? 8 : 8} style={{ fontSize: '11px', padding: '2px', border: '1px solid black', textAlign: 'right', fontWeight: 'bold' }}>Total:</td>
                            <td style={{ fontSize: '11px', padding: '2px', border: '1px solid black', textAlign: 'right', fontWeight: 'bold' }}>
                                {formatNumberWithCommas(totalGstAmount)}
                            </td>
                        </tr>
                        <tr>
                            <td colSpan={isSameState ? 9 : 6} style={{ fontSize: '11px', padding: '2px', border: '1px solid black', textAlign: 'left' }}> Tax Amount (in words): <strong style={{ fontSize: '11px' }}>{numberToWordsWithRupees(totalGstAmount)}</strong></td>
                        </tr>
                    </tbody>
                </table>

                {/* BOTTOM SECTION - PREVENT CUTTING ON SECOND PAGE */}
                <div className="sip-bottom-section" style={{
                    marginTop: '2px',
                    fontSize: '11px',
                    pageBreakInside: 'avoid',
                    breakInside: 'avoid'
                }}>
                    <div style={{ border: '1px solid black', padding: '2px', fontSize: '11px' }}>
                        <div style={{ margin: '0', fontSize: '11px', display: 'flex' }}>
                            {/* Left Side - Remarks & Declaration */}
                            <div style={{
                                width: '50%',
                                borderRight: '1px solid black',
                                minHeight: '120px',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'space-between',
                                padding: '0px 2px'
                            }}>
                                {/* Remarks Section */}
                                <div style={{ marginBottom: '8px' }}>
                                    <strong style={{ fontSize: '11px', display: 'block', marginBottom: '2px' }}>Remarks</strong>
                                    <div style={{
                                        border: '1px solid #ccc',
                                        padding: '4px',
                                        fontSize: '11px',
                                        lineHeight: '1.3',
                                        minHeight: '30px'
                                    }}>
                                        {salesData.remarks || 'N/A'}
                                    </div>
                                </div>

                                {/* Declaration Section */}
                                <div>
                                    <strong style={{ fontSize: '11px', display: 'block', marginBottom: '5px' }}>Declaration</strong>
                                    <div style={{
                                        borderTop: '1px solid black',
                                        paddingTop: '8px',
                                        fontSize: '11px',
                                        lineHeight: '1.3',
                                        textAlign: 'justify'
                                    }}>
                                        We declare that this invoice shows the actual price of the goods described and that all particulars are true and correct.
                                        Interest <strong style={{ fontSize: '11px' }}>@12% p.a</strong> will be charged if not paid within due date.
                                    </div>
                                </div>
                            </div>

                            {/* Right Side - Bank Details & Signature */}
                            <div style={{
                                width: '50%',
                                minHeight: '120px',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'space-between',
                                padding: '0px 2px'
                            }}>
                                <div style={{ marginBottom: '8px', padding: '2px' }}>
                                    <strong style={{ fontSize: '11px', display: 'block' }}>Company's Bank Details</strong>
                                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                        <tbody>
                                            <tr>
                                                <td style={{ fontWeight: 'bold', width: '35%', fontSize: '11px', padding: '2px', border: 'none' }}>Bank Name</td>
                                                <td style={{ padding: '2px', border: 'none' }}>:</td>
                                                <td style={{ padding: '2px', border: 'none' }}><strong style={{ fontSize: '11px' }}>{companyData.bankname || 'N/A'}</strong></td>
                                            </tr>
                                            <tr>
                                                <td style={{ fontWeight: 'bold', width: '35%', fontSize: '11px', padding: '3px 5px', border: 'none' }}>A/c No.</td>
                                                <td style={{ padding: '2px', border: 'none' }}>:</td>
                                                <td style={{ padding: '2px', border: 'none' }}><strong style={{ fontSize: '11px' }}> {companyData.accountnumber || 'N/A'}</strong></td>
                                            </tr>
                                            <tr>
                                                <td style={{ fontWeight: 'bold', width: '35%', fontSize: '11px', padding: '3px 5px', border: 'none' }}>Branch & IFS Code</td>
                                                <td style={{ padding: '2px', border: 'none' }}>:</td>
                                                <td style={{ padding: '2px', border: 'none' }}>
                                                    <strong style={{ fontSize: '11px' }}>
                                                        {companyData.branchname || 'N/A'} & {companyData.ifsccode || 'N/A'}
                                                    </strong>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td style={{ fontWeight: 'bold', width: '35%', fontSize: '11px', padding: '2px', border: 'none' }}>Company's PAN</td>
                                                <td style={{ padding: '2px', border: 'none' }}>:</td>
                                                <td style={{ padding: '2px', border: 'none' }}><strong style={{ fontSize: '11px' }}>{companyData.panno || 'N/A'}</strong></td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>

                                <div style={{
                                    borderTop: '1px solid black',
                                    textAlign: 'right',
                                    paddingTop: '40px',
                                    position: 'relative',
                                    marginTop: '8px',
                                    padding: '2px'
                                }}>
                                    <div style={{ marginBottom: '30px' }}>
                                        for <strong style={{ fontSize: '12px', fontStyle: 'italic', fontWeight: 'bold' }}>{companyData.companyname || 'SYNERGY Mechanics Pvt Ltd'}</strong>
                                    </div>
                                    <strong style={{ fontSize: '11px' }}>Authorised Signatory</strong>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Footer Note */}
                    <div style={{ textAlign: 'center', marginTop: '10px' }}>
                        <small style={{ fontSize: '10px' }}>This is a Computer Generated Invoice</small>
                    </div>
                </div>
            </div>
        </div >
    );
};

export default SalesInvoiceManufacturingDomesticPrint;